import { create } from 'zustand';
import type { SortDirection } from '@scio3/shared-types';
import { SORT_DIRECTION } from '@scio3/shared-types';

/**
 * Supported sort fields — MUST match backend `DeviceSort` input type in
 * `optisigns-open-api/src/app/device/device.inputs.ts`. The backend rejects
 * unknown sort keys (GraphQL input-type validation), which would make the
 * entire devices query fail silently from the user's perspective ("No devices"
 * with no error surfaced).
 */
export type MdmDeviceSortField = 'deviceName' | 'creationDate' | 'currentSelectionDate';

interface MdmDevicesSortState {
  sortField: MdmDeviceSortField;
  sortDirection: SortDirection;
  setSort: (field: MdmDeviceSortField, direction: SortDirection) => void;
}

const STORAGE_KEY = 'scio3-staff-mdm-devices-sorting';

const ALLOWED_FIELDS: readonly MdmDeviceSortField[] = [
  'deviceName',
  'creationDate',
  'currentSelectionDate',
];

function loadSort(): { sortField: MdmDeviceSortField; sortDirection: SortDirection } {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<MdmDevicesSortState>;
      // Migrate stale values (e.g. 'lastHeartBeat' from an earlier build) to
      // a backend-accepted default. Prevents the list from silently going
      // empty after the sort contract was narrowed.
      const field =
        parsed.sortField && ALLOWED_FIELDS.includes(parsed.sortField as MdmDeviceSortField)
          ? (parsed.sortField as MdmDeviceSortField)
          : 'creationDate';
      return {
        sortField: field,
        sortDirection: parsed.sortDirection ?? SORT_DIRECTION.DESC,
      };
    }
  } catch {
    // ignore parse errors — fall through to defaults
  }
  return { sortField: 'creationDate', sortDirection: SORT_DIRECTION.DESC };
}

const initial = loadSort();

export const useMdmDevicesSortStore = create<MdmDevicesSortState>((set) => ({
  sortField: initial.sortField,
  sortDirection: initial.sortDirection,
  setSort: (sortField, sortDirection) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ sortField, sortDirection }));
    } catch {
      // storage quota / private mode — ignore
    }
    set({ sortField, sortDirection });
  },
}));
